import React from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { HelpCircle, Mail, Tv, CreditCard, Globe, Headphones } from "lucide-react";

const faqData = [
  {
    category: "Commande & Livraison",
    icon: CreditCard,
    questions: [
      {
        question: "Que vais-je recevoir après achat ?",
        answer: "Un email automatique avec vos identifiants Xtream (nom d'utilisateur, mot de passe, URL). Vous pouvez les utiliser dès réception dans n'importe quelle application IPTV compatible."
      },
      {
        question: "Combien de temps pour recevoir mes identifiants ?",
        answer: "La livraison est immédiate. Dès que votre paiement est validé, vos identifiants sont générés et envoyés automatiquement par email. Pensez à vérifier vos spams si vous ne les trouvez pas."
      },
      {
        question: "Comment activer mon abonnement ?",
        answer: "Ouvrez votre application IPTV préférée (IPTV Smarters, TiviMate, GSE Smart IPTV, etc.), saisissez les identifiants Xtream reçus par email (serveur URL, nom d'utilisateur, mot de passe), et vous êtes prêt à regarder."
      }
    ]
  },
  {
    category: "Test gratuit",
    icon: Mail,
    questions: [
      {
        question: "Puis-je tester avant d'acheter ?",
        answer: "Oui. Entrez simplement votre adresse email dans le formulaire prévu sur notre site. Vous recevrez un accès temporaire pour tester la qualité du service, sans engagement ni carte bancaire."
      },
      {
        question: "Combien de temps dure le test gratuit ?",
        answer: "Le test gratuit vous donne accès à l'ensemble du service pendant 24 heures. C'est suffisant pour tester la qualité des chaînes, la VOD et la compatibilité avec votre appareil."
      }
    ]
  },
  {
    category: "Compatibilité",
    icon: Tv,
    questions: [
      {
        question: "Sur quels appareils puis-je utiliser le service ?",
        answer: "Le service est compatible avec : Smart TV (LG, Samsung, Sony, etc.), Android Box, Amazon Firestick / Fire TV, PC et Mac, tablettes et smartphones (Android et iOS). Tout appareil supportant une application IPTV Xtream peut être utilisé."
      },
      {
        question: "Quelle application dois-je utiliser ?",
        answer: "Vous pouvez utiliser n'importe quelle application IPTV compatible Xtream Codes : IPTV Smarters Pro, TiviMate, GSE Smart IPTV, Xtream IPTV, ou l'application native de votre Smart TV. Consultez nos tutoriels pour des guides d'installation détaillés."
      },
      {
        question: "Puis-je regarder sur plusieurs appareils ?",
        answer: "Chaque abonnement inclut une connexion simultanée. Si vous souhaitez regarder sur plusieurs écrans en même temps, vous pouvez souscrire à des connexions supplémentaires."
      }
    ]
  },
  {
    category: "Service & Qualité",
    icon: Globe,
    questions: [
      {
        question: "Le service est-il disponible à l'international ?",
        answer: "Oui, vous pouvez utiliser le service partout dans le monde, tant que vous avez une connexion Internet stable. Nos serveurs sont optimisés pour offrir une bonne qualité depuis n'importe quel pays."
      },
      {
        question: "La qualité est vraiment HD / 4K ?",
        answer: "Oui. La majorité de nos chaînes sont disponibles en HD, et les chaînes premium ainsi que les contenus VOD sont proposés en 4K quand disponibles. La qualité s'adapte automatiquement à votre connexion."
      },
      {
        question: "Combien de chaînes sont disponibles ?",
        answer: "Notre service propose plus de 15 000 chaînes internationales, couvrant les principales chaînes françaises, européennes et mondiales. Vous avez également accès à une bibliothèque VOD avec des milliers de films et séries."
      }
    ]
  },
  {
    category: "Support",
    icon: Headphones,
    questions: [
      {
        question: "Comment contacter le support ?",
        answer: "Le support client est disponible directement sur le site via notre formulaire de contact. Aucun contact externe (WhatsApp, Telegram) n'est nécessaire. Nous répondons généralement dans les quelques heures."
      },
      {
        question: "Que faire si j'ai un problème technique ?",
        answer: "Consultez d'abord nos tutoriels d'installation pour votre appareil. Si le problème persiste, contactez notre support via le formulaire du site en décrivant votre problème. Nous vous aiderons à résoudre le souci rapidement."
      }
    ]
  }
];

const Faq = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.flatMap(category => 
      category.questions.map(q => ({
        "@type": "Question",
        "name": q.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": q.answer
        }
      }))
    )
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://roidustreaming.fr" },
      { "@type": "ListItem", "position": 2, "name": "FAQ", "item": "https://roidustreaming.fr/faq" }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>FAQ IPTV – Réponses aux questions courantes | Roi du Streaming</title>
        <meta name="description" content="Découvrez comment activer votre IPTV, recevoir vos codes Xtream, tester gratuitement, et utiliser le service. Toutes les réponses à vos questions." />
        <meta name="keywords" content="faq iptv, questions iptv, comment activer iptv, xtream codes aide, test iptv gratuit, support iptv" />
        <link rel="canonical" href="https://roidustreaming.fr/faq" />
        <meta property="og:title" content="FAQ IPTV – Réponses aux questions courantes" />
        <meta property="og:description" content="Découvrez comment activer votre IPTV, recevoir vos codes Xtream, tester gratuitement." />
        <meta property="og:url" content="https://roidustreaming.fr/faq" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <Header />

      <main className="py-12 md:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumbs */}
          <nav className="text-sm text-muted-foreground mb-8" aria-label="Fil d'Ariane">
            <ol className="flex flex-wrap gap-2">
              <li><Link to="/" className="hover:text-primary transition-colors">Accueil</Link></li>
              <li>/</li>
              <li className="text-foreground">FAQ</li>
            </ol>
          </nav>

          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
              Foire aux questions – <span className="gradient-text">IPTV Roi du Streaming</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Retrouvez les réponses aux questions les plus fréquentes sur notre service IPTV, 
              l'activation, la compatibilité et le support.
            </p>
          </div>

          {/* FAQ Categories */}
          <div className="space-y-8">
            {faqData.map((category, categoryIndex) => (
              <div key={categoryIndex} className="card-premium rounded-2xl overflow-hidden">
                <div className="p-6 border-b border-border/30 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <category.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-xl font-semibold">{category.category}</h2>
                </div>
                <Accordion type="single" collapsible className="px-6">
                  {category.questions.map((faq, faqIndex) => (
                    <AccordionItem
                      key={faqIndex}
                      value={`${categoryIndex}-${faqIndex}`}
                      className="border-border/30"
                    >
                      <AccordionTrigger className="py-4 hover:no-underline text-left font-medium hover:text-primary transition-colors">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="pb-4 text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="mt-12 text-center card-premium p-8 rounded-2xl">
            <h3 className="text-xl font-semibold mb-3">Vous n'avez pas trouvé votre réponse ?</h3>
            <p className="text-muted-foreground mb-6">
              Notre équipe support est disponible pour vous aider directement sur le site.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/nos-offres">
                <Button className="btn-primary text-white font-semibold px-8">
                  Voir nos offres
                </Button>
              </Link>
              <Link to="/tutoriels">
                <Button variant="outline" className="px-8">
                  Consulter les tutoriels
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Faq;
